import {
  ChevronRight16Icon,
  DotsGridIcon,
  HelpCircleSmIcon,
  ListSmIcon,
  LogOut03Icon,
  Menu04Icon,
  Settings02Icon,
  ShoppingCart03Icon,
  Users02SmIcon,
} from 'assets/icons';
import { ShareDialog } from 'common/share-dialog';
import { supportEmail } from 'config/constants';
import { AppContext } from 'context';
import { classnames, copyToClipboard, getInitials } from 'helpers/utils';
import { vaultAccess, vaultShareTarget } from 'helpers/sharing';
import { useClickOutside } from 'hooks/click-outside';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { goToPage, RouteName } from 'routes';
import { VaultInfo } from './components/vault-info';
import styles from './topnav-dropdown-menu.module.scss';

interface TopnavDropdownMenuProps {
  onLogout: () => void;
  // When given, the menu is the vault's: its info card (cover, name, Share,
  // and storage when nearly full) closes the menu. Omitted outside a vault
  // (e.g. the all-vaults navbar).
  vault?: VaultType;
}

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  // Right-column items lead to another section and carry a chevron.
  chevron?: boolean;
  // The screen the user is on.
  current?: boolean;
  // Copies the text on click and shows the success label for a moment.
  copy?: { text: string; successLabel: string };
}

// How long the "copied" confirmation stays on the item.
const COPIED_FEEDBACK_MS = 2000;

export const TopnavDropdownMenu: React.FC<TopnavDropdownMenuProps> = ({
  onLogout,
  vault,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const { state } = useContext(AppContext);
  const { pathname } = useLocation();
  const { user } = state.data;
  const access = vaultAccess(user, vault);

  useClickOutside(menuRef, () => setIsOpen(false));
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const handleCopy = async (item: MenuItem) => {
    if (!item.copy) return;
    try {
      await copyToClipboard(item.copy.text);
    } catch {
      // Clipboard access was refused (permissions, an iframe, some Safari
      // cases): don't claim success, hand the address to the mail client.
      window.location.href = `mailto:${item.copy.text}`;
      return;
    }
    setCopiedLabel(item.label);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(
      () => setCopiedLabel(null),
      COPIED_FEEDBACK_MS,
    );
  };

  // Navigating closes the menu; the copy item stays open to show its feedback.
  const go = (action: () => void) => () => {
    setIsOpen(false);
    action();
  };

  // Inside a vault, Collaborators opens with that vault listed first and
  // expanded.
  const vaultId = vault?.id ?? null;
  const accountItems: MenuItem[] = [
    {
      label: 'Settings',
      icon: <Settings02Icon />,
      // Inside a vault, its owner and editors get the vault's settings.
      onClick: go(() =>
        vault && access.canManageVault
          ? goToPage(RouteName.VaultSettings, { id: vault.id })
          : goToPage(RouteName.Account),
      ),
    },
    {
      label: 'Collaborators',
      icon: <Users02SmIcon />,
      current: pathname.startsWith('/collaborators'),
      onClick: go(() =>
        goToPage(RouteName.Collaborators, {}, vaultId ? { vaultId } : {}),
      ),
    },
    {
      label: 'Support',
      icon: <HelpCircleSmIcon />,
      onClick: () => {},
      copy: { text: supportEmail, successLabel: 'Email copied' },
    },
  ];
  const sectionItems: MenuItem[] = [
    {
      label: 'All Vaults',
      icon: <DotsGridIcon />,
      chevron: true,
      current: pathname === '/vaults',
      onClick: go(() => goToPage(RouteName.Vaults)),
    },
    {
      label: 'Store',
      icon: <ShoppingCart03Icon />,
      chevron: true,
      current: pathname.startsWith('/store'),
      onClick: go(() => goToPage(RouteName.Store)),
    },
    ...(vault
      ? []
      : [
          {
            label: 'Prompt Templates',
            icon: <ListSmIcon />,
            chevron: true,
            current: pathname.startsWith('/templates'),
            onClick: go(() => goToPage(RouteName.Templates)),
          },
        ]),
  ];

  const renderItem = (item: MenuItem) => {
    const copied = !!item.copy && copiedLabel === item.label;
    return (
      <div className={styles.itemWrap} key={item.label}>
        <button
          type="button"
          className={classnames(styles.item, {
            [styles.itemCurrent]: !!item.current,
          })}
          onClick={item.copy ? () => handleCopy(item) : item.onClick}
          aria-current={item.current ? 'page' : undefined}
        >
          <span className={styles.itemIcon}>{item.icon}</span>
          <span className={styles.itemLabel}>
            {copied && item.copy ? item.copy.successLabel : item.label}
          </span>
          {item.chevron && !item.current && (
            <span className={styles.itemChevron}>
              <ChevronRight16Icon />
            </span>
          )}
        </button>
      </div>
    );
  };

  const initials = getInitials(user.name).replaceAll(' ', '');

  return (
    <div className={styles.container} ref={menuRef}>
      <button
        type="button"
        className={classnames(styles.trigger, {
          [styles.triggerOpen]: isOpen,
        })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Account menu"
      >
        <span className={styles.menuIcon}>
          <Menu04Icon />
        </span>
        <span className={styles.avatar}>{initials}</span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <div className={styles.account}>
              <span className={styles.accountAvatar}>{initials}</span>
              <div className={styles.accountText}>
                <span className={styles.accountName}>{user.name}</span>
                <span className={styles.accountEmail} title={user.email}>
                  {user.email}
                </span>
              </div>
            </div>
            <button type="button" className={styles.signOut} onClick={onLogout}>
              <LogOut03Icon />
              Sign out
            </button>
          </div>

          <div className={styles.nav}>
            <div className={styles.columnLeft}>
              {accountItems.map(renderItem)}
            </div>
            <div className={styles.columnDivider} />
            <div className={styles.columnRight}>
              {sectionItems.map(renderItem)}
            </div>
          </div>

          {vault && (
            <VaultInfo
              vault={vault}
              onShare={
                access.canShareVault
                  ? () => {
                      setIsOpen(false);
                      setShowShare(true);
                    }
                  : undefined
              }
            />
          )}
        </div>
      )}

      {showShare && vault && (
        <ShareDialog
          target={vaultShareTarget(vault)}
          choices={[
            vaultShareTarget(vault),
            ...(vault.allAlbums || [])
              .filter((album) => !album.isDefault)
              .map((album) => ({
                type: 'album' as const,
                vaultId: vault.id,
                id: album.id,
                name: album.name,
                isPrivate: album.isPrivate,
              })),
          ]}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
};
